import { ApiError, getHomeZoneStatus, getHomeZoneStatusQueryKey, type HomeZoneStatusResponse } from '@platform/api';
import { type UseSuspenseQueryResult, useSuspenseQuery } from '@tanstack/react-query';

export type HomeNetworkStatus = Omit<HomeZoneStatusResponse, 'nextChangeAllowedAt'> & { nextChangeAllowedAt: Date | null };

async function fetchHomeNetworkStatus(signal: AbortSignal | undefined): Promise<HomeNetworkStatus | null> {
  try {
    const { data } = await getHomeZoneStatus({ signal, throwOnError: true });
    if (!data) {
      return null;
    }
    return { ...data, nextChangeAllowedAt: data.nextChangeAllowedAt ? new Date(data.nextChangeAllowedAt) : null };
  } catch (err) {
    // 404 = no home network set — a real empty state, not an error.
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

// Suffixed per the query-key rule (apps/hub/CLAUDE.md): this queryFn reshapes
// 404 -> null, so it must not share a cache entry with a plain consumer of
// getHomeZoneStatusOptions.
const homeNetworkStatusQueryKey = [...getHomeZoneStatusQueryKey(), 'or-null'] as const;

export function getHomeNetworkStatusQueryOptions() {
  return {
    queryKey: homeNetworkStatusQueryKey,
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchHomeNetworkStatus(signal),
  };
}

/** Suspends until the authenticated customer's home-network status resolves; `data` is `null` when none is set. */
export function useHomeNetworkStatus(): UseSuspenseQueryResult<HomeNetworkStatus | null> {
  return useSuspenseQuery(getHomeNetworkStatusQueryOptions());
}
